/**
 * component-see-it-styled.js -- UGC video reel carousel for Mirai Online.
 *
 * <see-it-styled> custom element:
 *  - Native scroll-snap track; desktop prev/next arrows scroll one card at a time
 *    (same pattern as <new-arrivals-carousel>).
 *  - Click-to-play cards. Videos ship preload="none", so nothing is fetched until
 *    a shopper actually taps a card.
 *  - Exclusive playback: starting a card pauses the one that was playing, here and
 *    in any other <see-it-styled> on the page. Two reels talking at once is the
 *    single worst failure mode of this section.
 *  - Per-card mute toggle. Cards start muted (browsers refuse audible play() from a
 *    non-gesture context, and an unmuted play() rejection would leave the poster up
 *    with no feedback), so the speaker button is the shopper's opt-in to audio.
 *  - IntersectionObserver pauses a card scrolled out of view — no audio from a
 *    section the shopper has left behind.
 *  - Arrow aria-disabled (not the disabled attribute) keeps them Tab-focusable at
 *    the track bounds.
 *
 * Security notes:
 *  - No user input is read or reflected; no innerHTML.
 *  - Video sources come from Shopify's CDN via Liquid, never from JS.
 */

/* ============================================================
   MODULE-LEVEL PLAYBACK REGISTRY
   Exclusive playback has to span instances: two <see-it-styled> sections on one
   page (or a theme-editor re-render leaving a stale one mid-teardown) would each
   keep their own "currently playing" pointer and happily play over each other.
   ============================================================ */

/** @type {HTMLVideoElement | null} */
let currentlyPlaying = null;

function pauseCurrent(except) {
  if (currentlyPlaying && currentlyPlaying !== except) {
    currentlyPlaying.pause();
  }
}

class SeeItStyled extends HTMLElement {
  constructor() {
    super();
    this._track = null;
    this._prevBtn = null;
    this._nextBtn = null;
    this._cards = [];
    this._observer = null;
    this._scrollHandler = null;
    this._boundPrev = null;
    this._boundNext = null;
    this._boundSectionLoad = null;
    // Per-card listener handles, kept so _teardown() can actually remove them.
    // Fresh closures each _init() are NOT deduped by addEventListener, so without
    // this a theme-editor reinit double-binds every play button.
    this._cardBindings = [];
  }

  connectedCallback() {
    this._init();
    // Shopify fires shopify:section:load on the section wrapper and on document,
    // not on this inner element, and it does not propagate down. Listen on document
    // with a containment self-check so only THIS component's section reinits.
    this._boundSectionLoad = (event) => {
      if (event.target && event.target.contains(this)) this._reinit();
    };
    document.addEventListener('shopify:section:load', this._boundSectionLoad);
  }

  disconnectedCallback() {
    this._teardown();
    if (this._boundSectionLoad) {
      document.removeEventListener('shopify:section:load', this._boundSectionLoad);
      this._boundSectionLoad = null;
    }
  }

  _reinit() {
    this._teardown();
    this._init();
  }

  _teardown() {
    if (this._scrollHandler && this._track) {
      this._track.removeEventListener('scroll', this._scrollHandler);
    }
    if (this._prevBtn && this._boundPrev) this._prevBtn.removeEventListener('click', this._boundPrev);
    if (this._nextBtn && this._boundNext) this._nextBtn.removeEventListener('click', this._boundNext);

    for (const { el, type, handler } of this._cardBindings) {
      el.removeEventListener(type, handler);
    }
    this._cardBindings = [];

    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }

    // A section removed mid-playback (theme editor re-render) must not leave the
    // registry pointing at a detached <video> that can never be paused again.
    for (const { video } of this._cards) {
      if (video === currentlyPlaying) {
        video.pause();
        currentlyPlaying = null;
      }
    }
    this._cards = [];
  }

  _init() {
    this._track = this.querySelector('[data-carousel-track]');
    this._prevBtn = this.querySelector('[data-carousel-prev]');
    this._nextBtn = this.querySelector('[data-carousel-next]');

    if (!this._track) return;

    this._scrollHandler = this._updateArrows.bind(this);
    this._track.addEventListener('scroll', this._scrollHandler, { passive: true });

    this._boundPrev = () => this._scroll(-1);
    this._boundNext = () => this._scroll(1);
    if (this._prevBtn) this._prevBtn.addEventListener('click', this._boundPrev);
    if (this._nextBtn) this._nextBtn.addEventListener('click', this._boundNext);

    this._initCards();
    this._updateArrows();
  }

  _initCards() {
    const cards = Array.from(this.querySelectorAll('[data-sis-card]'));

    this._cards = cards
      .map((card) => ({
        card,
        video: card.querySelector('[data-sis-video]'),
        playBtn: card.querySelector('[data-sis-play]'),
        muteBtn: card.querySelector('[data-sis-mute]')
      }))
      // Placeholder cards (no video picked yet in the theme editor) have nothing
      // to wire up — skip them rather than guarding every handler below.
      .filter((entry) => entry.video);

    for (const entry of this._cards) {
      const { video, playBtn, muteBtn } = entry;

      if (playBtn) {
        this._bind(playBtn, 'click', () => this._toggle(entry));
      }

      if (muteBtn) {
        this._bind(muteBtn, 'click', (event) => {
          // The mute button sits on top of the full-frame play button. Without
          // this, muting would also toggle playback underneath it.
          event.stopPropagation();
          this._toggleMute(entry);
        });
      }

      // Drive the card's visual state from the video's real events, not from what
      // we think we did: play() is async and can be rejected (low power mode, an
      // autoplay policy), and pausing can also come from the registry or the
      // IntersectionObserver. Listening to the source of truth keeps the play
      // icon honest in all of those paths.
      this._bind(video, 'play', () => {
        pauseCurrent(video);
        currentlyPlaying = video;
        entry.card.classList.add('sis__card--playing');
        this._syncPlayLabel(entry, true);
      });

      this._bind(video, 'pause', () => {
        if (currentlyPlaying === video) currentlyPlaying = null;
        entry.card.classList.remove('sis__card--playing');
        this._syncPlayLabel(entry, false);
      });
    }

    this._initObserver();
  }

  /** addEventListener + record the handle for symmetric removal in _teardown(). */
  _bind(el, type, handler) {
    el.addEventListener(type, handler);
    this._cardBindings.push({ el, type, handler });
  }

  _toggle(entry) {
    const { video } = entry;

    if (video.paused) {
      pauseCurrent(video);
      // Muted play() is allowed without a gesture and effectively never rejects,
      // but it still returns a promise — swallow the rejection so a blocked play
      // can't surface as an unhandled rejection in the console.
      const played = video.play();
      if (played && typeof played.catch === 'function') played.catch(() => {});
    } else {
      video.pause();
    }
  }

  _toggleMute(entry) {
    const { video, muteBtn } = entry;
    video.muted = !video.muted;

    if (muteBtn) {
      muteBtn.classList.toggle('sis__mute--on', !video.muted);

      // The label names the ACTION, so it flips with state: a muted card offers
      // "Unmute", an audible one offers "Mute".
      const label = video.muted ? muteBtn.dataset.labelUnmute : muteBtn.dataset.labelMute;
      if (label) muteBtn.setAttribute('aria-label', label);
    }

    // Unmuting a paused card is the shopper asking to hear it — start it.
    if (!video.muted && video.paused) this._toggle(entry);
  }

  _syncPlayLabel(entry, playing) {
    const { playBtn } = entry;
    if (!playBtn) return;

    const label = playing ? playBtn.dataset.labelPause : playBtn.dataset.labelPlay;
    if (label) playBtn.setAttribute('aria-label', label);
  }

  /** Pause a card once it is mostly scrolled out of the viewport. */
  _initObserver() {
    if (!('IntersectionObserver' in window) || this._cards.length === 0) return;

    this._observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) continue;
          const video = entry.target;
          if (!video.paused) video.pause();
        }
      },
      // Fire once the card is more than half gone — pausing the instant a single
      // pixel leaves the viewport would kill playback during an ordinary scroll.
      { threshold: 0.5 }
    );

    for (const { video } of this._cards) {
      this._observer.observe(video);
    }
  }

  /**
   * Scroll the track by one card width.
   * @param {number} direction  -1 for prev, +1 for next
   */
  _scroll(direction) {
    if (!this._track) return;

    const item = this._track.querySelector('.sis__item');
    if (!item) return;

    const gap = parseInt(getComputedStyle(this._track).getPropertyValue('gap') || '0', 10) || 0;

    this._track.scrollBy({
      left: direction * (item.offsetWidth + gap),
      behavior: 'smooth'
    });
  }

  /** Reflect scroll position on the arrows (aria-disabled + dimmed at the bounds). */
  _updateArrows() {
    if (!this._track) return;

    const { scrollLeft, clientWidth, scrollWidth } = this._track;
    const atStart = scrollLeft <= 4;
    const atEnd = scrollLeft + clientWidth >= scrollWidth - 4;

    if (this._prevBtn) {
      this._prevBtn.setAttribute('aria-disabled', String(atStart));
      this._prevBtn.style.opacity = atStart ? '0.4' : '1';
    }

    if (this._nextBtn) {
      this._nextBtn.setAttribute('aria-disabled', String(atEnd));
      this._nextBtn.style.opacity = atEnd ? '0.4' : '1';
    }
  }
}

if (!customElements.get('see-it-styled')) {
  customElements.define('see-it-styled', SeeItStyled);
}
